import type { CompanionDecisionAction } from "./decision";
import type { CompanionDestinationId } from "./navigation";
import type { CompanionDiscoveryRecommendation, CompanionRecommendationEmptyReason } from "./recommendation-types";
import type { CompanionRecordSourceHealth } from "./record-types";
import type {
  CompanionAroundYouRelevance,
  CompanionAroundYouResultKind,
} from "./around-you";
import type { PublicEventTrustState } from "@/lib/public-events/presentation";
import type { PublicEventCategory } from "@/lib/public-events/types";

export type CompanionAnalyticsEvent = {
  name:
    | "companion_viewed"
    | "companion_landing_viewed"
    | "companion_signup_opened"
    | "companion_sport_saved"
    | "companion_setup_progress"
    | "recommendation_viewed"
    | "recommendation_actioned"
    | "companion_destination_opened"
    | "companion_section_load_failed"
    | "companion_section_retry"
    | "discovery_viewed"
    | "discovery_actioned"
    | "roll_call_updated"
    | "meetup_created"
    | "meetup_rsvp_updated"
    | "status_pin_updated"
    | "social_moment_opened"
    | "steeze_toggled"
    | "companion_people_section_failed"
    | "event_submission_completed"
    | "forum_recommendation_opened"
    | "event_opened"
    | "record_opened"
    | "tracking_started"
    | "manual_log_started";
  properties: Record<string, string | number | boolean | null>;
};

export type CompanionAnalyticsActionKind =
  | "call"
  | "plan"
  | "track"
  | "conditions"
  | "setup";

export type CompanionSectionFailureSource =
  | "saved-places"
  | "forecasts"
  | "nearby"
  | "device"
  | "account"
  | "legacy"
  | "awards"
  | "season"
  | "personal-bests"
  | "availability"
  | "briefings"
  | "calls"
  | "social"
  | "passport";

type CompanionManagedDestination = "plan" | "record" | "settings";
type CompanionLegacySection = "people" | "today-critical" | "around-you";

type CompanionSectionLoadFailedInput = {
  destination: CompanionManagedDestination;
  source: CompanionSectionFailureSource;
};

type CompanionSectionRetryInput = CompanionSectionLoadFailedInput & {
  outcome: "success" | "error";
};

const COMPANION_MANAGED_DESTINATIONS: readonly CompanionManagedDestination[] = [
  "plan",
  "record",
  "settings",
];

const COMPANION_SECTION_FAILURE_SOURCES: readonly CompanionSectionFailureSource[] = [
  "saved-places",
  "forecasts",
  "nearby",
  "device",
  "account",
  "legacy",
  "awards",
  "season",
  "personal-bests",
  "availability",
  "briefings",
  "calls",
  "social",
  "passport",
];

const COMPANION_LEGACY_SECTIONS: readonly CompanionLegacySection[] = [
  "people",
  "today-critical",
  "around-you",
];

type CompanionAnalyticsConfidenceState = "high" | "medium" | "low" | "unknown";

type CompanionAnalyticsFreshnessState =
  | "updated-today"
  | "updated-yesterday"
  | "stale"
  | "missing"
  | "unknown";

export type DiscoveryAnalyticsInput = {
  suitability: CompanionDiscoveryRecommendation["suitability"];
  score: number;
  hasDistinctWindow: boolean;
};

export function companionViewedEvent(
  destination: CompanionDestinationId,
  state: "empty" | "setup" | "ready",
): CompanionAnalyticsEvent {
  return {
    name: "companion_viewed",
    properties: {
      destination,
      view_state: state,
    },
  };
}

export function recommendationViewedEvent(input: {
  decisionAction: CompanionDecisionAction;
  confidence: string;
  freshness: string;
  emptyReason?: CompanionRecommendationEmptyReason | null;
}): CompanionAnalyticsEvent {
  return {
    name: "recommendation_viewed",
    properties: {
      decision_action: input.decisionAction,
      ...(input.emptyReason ? { empty_reason: input.emptyReason } : {}),
      confidence_state: companionAnalyticsConfidenceState(input.confidence),
      freshness_state: companionAnalyticsFreshnessState(input.freshness),
    },
  };
}

export function recommendationActionedEvent(input: {
  actionKind: CompanionAnalyticsActionKind;
  decisionAction: CompanionDecisionAction;
  confidence: string;
  freshness: string;
  emptyReason?: CompanionRecommendationEmptyReason | null;
}): CompanionAnalyticsEvent {
  return {
    name: "recommendation_actioned",
    properties: {
      action_kind: input.actionKind,
      decision_action: input.decisionAction,
      ...(input.emptyReason ? { empty_reason: input.emptyReason } : {}),
      confidence_state: companionAnalyticsConfidenceState(input.confidence),
      freshness_state: companionAnalyticsFreshnessState(input.freshness),
    },
  };
}

export function companionDestinationOpenedEvent(
  destination: CompanionDestinationId,
): CompanionAnalyticsEvent {
  return {
    name: "companion_destination_opened",
    properties: { destination },
  };
}

export function companionSectionLoadFailedEvent(
  input: CompanionSectionLoadFailedInput,
): CompanionAnalyticsEvent;
export function companionSectionLoadFailedEvent(
  section: CompanionLegacySection,
  unavailableSourceCount: number,
): CompanionAnalyticsEvent;
export function companionSectionLoadFailedEvent(
  inputOrSection: unknown,
  unavailableSourceCount?: number,
): CompanionAnalyticsEvent {
  if (isCompanionSectionLoadFailedInput(inputOrSection)) {
    return {
      name: "companion_section_load_failed",
      properties: {
        destination: inputOrSection.destination,
        source: inputOrSection.source,
      },
    };
  }

  if (!isCompanionLegacySection(inputOrSection)) {
    return {
      name: "companion_section_load_failed",
      properties: {},
    };
  }

  return {
    name: "companion_section_load_failed",
    properties: {
      section: inputOrSection,
      unavailable_source_count: unavailableSourceCount ?? 0,
    },
  };
}

export function companionSectionRetryEvent(
  input: CompanionSectionRetryInput,
): CompanionAnalyticsEvent;
export function companionSectionRetryEvent(
  section: CompanionLegacySection,
): CompanionAnalyticsEvent;
export function companionSectionRetryEvent(
  inputOrSection: unknown,
): CompanionAnalyticsEvent {
  if (isCompanionSectionRetryInput(inputOrSection)) {
    return {
      name: "companion_section_retry",
      properties: {
        destination: inputOrSection.destination,
        source: inputOrSection.source,
        outcome: inputOrSection.outcome,
      },
    };
  }

  if (!isCompanionLegacySection(inputOrSection)) {
    return {
      name: "companion_section_retry",
      properties: {},
    };
  }

  return {
    name: "companion_section_retry",
    properties: { section: inputOrSection },
  };
}

function isCompanionSectionLoadFailedInput(
  value: unknown,
): value is CompanionSectionLoadFailedInput {
  if (!isUnknownRecord(value)) return false;
  return isCompanionManagedDestination(value.destination) &&
    isCompanionSectionFailureSource(value.source);
}

function isCompanionSectionRetryInput(
  value: unknown,
): value is CompanionSectionRetryInput {
  if (!isUnknownRecord(value)) return false;
  return isCompanionManagedDestination(value.destination) &&
    isCompanionSectionFailureSource(value.source) &&
    (value.outcome === "success" || value.outcome === "error");
}

function isCompanionManagedDestination(
  value: unknown,
): value is CompanionManagedDestination {
  return typeof value === "string" &&
    COMPANION_MANAGED_DESTINATIONS.some((destination) => destination === value);
}

function isCompanionSectionFailureSource(
  value: unknown,
): value is CompanionSectionFailureSource {
  return typeof value === "string" &&
    COMPANION_SECTION_FAILURE_SOURCES.some((source) => source === value);
}

function isCompanionLegacySection(
  value: unknown,
): value is CompanionLegacySection {
  return typeof value === "string" &&
    COMPANION_LEGACY_SECTIONS.some((section) => section === value);
}

function isUnknownRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function discoveryViewedEvent(
  input: DiscoveryAnalyticsInput,
): CompanionAnalyticsEvent {
  return {
    name: "discovery_viewed",
    properties: discoveryProperties(input),
  };
}

export function discoveryActionedEvent(
  input: DiscoveryAnalyticsInput & {
    actionKind: "conditions" | "known";
  },
): CompanionAnalyticsEvent {
  return {
    name: "discovery_actioned",
    properties: {
      suitability_state: input.suitability,
      action_kind: input.actionKind,
      score_band: discoveryScoreBand(input.score),
      has_distinct_window: input.hasDistinctWindow,
    },
  };
}

export function rollCallUpdatedEvent(input: {
  commitmentType: string;
  target: "today" | "tomorrow";
}): CompanionAnalyticsEvent {
  return {
    name: "roll_call_updated",
    properties: {
      commitment_type: input.commitmentType,
      target_day: input.target,
    },
  };
}

export function meetupCreatedEvent(input: {
  locationType: string;
}): CompanionAnalyticsEvent {
  return {
    name: "meetup_created",
    properties: { location_type: input.locationType },
  };
}

export function meetupRsvpUpdatedEvent(input: {
  state: "in" | "maybe" | "cant_make_it";
}): CompanionAnalyticsEvent {
  return {
    name: "meetup_rsvp_updated",
    properties: { rsvp_state: input.state },
  };
}

export function statusPinUpdatedEvent(input: {
  statusType: string;
}): CompanionAnalyticsEvent {
  return {
    name: "status_pin_updated",
    properties: { status_type: input.statusType },
  };
}

export function socialMomentOpenedEvent(input: {
  momentType: string;
}): CompanionAnalyticsEvent {
  return {
    name: "social_moment_opened",
    properties: { moment_type: input.momentType },
  };
}

export function steezeToggledEvent(input: {
  active: boolean;
}): CompanionAnalyticsEvent {
  return {
    name: "steeze_toggled",
    properties: { active: input.active },
  };
}

export function peopleSectionFailedEvent(input: {
  source: "roll-call" | "meetups" | "statuses" | "moments";
}): CompanionAnalyticsEvent {
  return {
    name: "companion_people_section_failed",
    properties: { source: input.source },
  };
}

export function eventSubmissionCompletedEvent(input: {
  eventCategory: PublicEventCategory;
  resultKind: "pending";
}): CompanionAnalyticsEvent {
  return {
    name: "event_submission_completed",
    properties: {
      event_category: input.eventCategory,
      result_kind: input.resultKind,
    },
  };
}

export function forumRecommendationOpenedEvent(input: {
  relevanceKind: CompanionAroundYouRelevance;
  resultKind: CompanionAroundYouResultKind;
}): CompanionAnalyticsEvent {
  return {
    name: "forum_recommendation_opened",
    properties: {
      item_kind: "forum",
      relevance_kind: input.relevanceKind,
      result_kind: input.resultKind,
    },
  };
}

export function eventOpenedEvent(input: {
  relevanceKind: CompanionAroundYouRelevance;
  verificationState: PublicEventTrustState;
  eventCategory: PublicEventCategory;
  resultKind: CompanionAroundYouResultKind;
}): CompanionAnalyticsEvent {
  return {
    name: "event_opened",
    properties: {
      item_kind: "event",
      relevance_kind: input.relevanceKind,
      verification_state: input.verificationState,
      event_category: input.eventCategory,
      result_kind: input.resultKind,
    },
  };
}

export function recordOpenedEvent(
  health: CompanionRecordSourceHealth,
): CompanionAnalyticsEvent {
  return {
    name: "record_opened",
    properties: {
      device_state: companionRecordDeviceState(health.device),
      account_state: companionRecordReadyState(health.account),
      legacy_state: companionRecordReadyState(health.legacy),
      awards_state: companionRecordReadyState(health.awards),
      season_state: companionRecordSeasonState(health.season),
      personal_bests_state: companionRecordReadyState(health.personalBests),
    },
  };
}

export function trackingStartedEvent(): CompanionAnalyticsEvent {
  return {
    name: "tracking_started",
    properties: {},
  };
}

export function manualLogStartedEvent(): CompanionAnalyticsEvent {
  return {
    name: "manual_log_started",
    properties: {},
  };
}

function discoveryProperties(
  input: DiscoveryAnalyticsInput,
): CompanionAnalyticsEvent["properties"] {
  return {
    suitability_state: input.suitability,
    score_band: discoveryScoreBand(input.score),
    has_distinct_window: input.hasDistinctWindow,
  };
}

function companionRecordDeviceState(
  value: unknown,
): CompanionRecordSourceHealth["device"] {
  if (value === "loading" || value === "ready") return value;
  return "unavailable";
}

function companionRecordReadyState(
  value: unknown,
): CompanionRecordSourceHealth["account"] {
  return value === "ready" ? "ready" : "unavailable";
}

function companionRecordSeasonState(
  value: unknown,
): CompanionRecordSourceHealth["season"] {
  if (value === "ready" || value === "pending") return value;
  return "unavailable";
}

function discoveryScoreBand(
  score: number,
): "strong" | "firing" | "exceptional" {
  if (score >= 9) return "exceptional";
  if (score >= 8) return "firing";
  return "strong";
}

function companionAnalyticsConfidenceState(
  value: string,
): CompanionAnalyticsConfidenceState {
  const states: Record<string, Exclude<CompanionAnalyticsConfidenceState, "unknown">> = {
    "high confidence": "high",
    "medium confidence": "medium",
    "low confidence": "low",
  };

  return states[value.trim().toLowerCase()] ?? "unknown";
}

function companionAnalyticsFreshnessState(
  value: string,
): CompanionAnalyticsFreshnessState {
  const states: Record<string, Exclude<CompanionAnalyticsFreshnessState, "unknown">> = {
    "updated today": "updated-today",
    "updated in the last six hours": "updated-today",
    "stored update is over six hours old": "stale",
    "updated yesterday": "updated-yesterday",
    stale: "stale",
    "no fresh score": "missing",
  };

  return states[value.trim().toLowerCase()] ?? "unknown";
}
